import { useEffect, useState } from "react";
import { LockSimple, WarningOctagon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import StatusControl from "@/components/admin/StatusControl";
import CountryPicker from "@/components/admin/CountryPicker";
import { api } from "@/lib/api";
import { PRECEDENCE_HELP, REGION_TYPES, isSanctioned } from "@/lib/constants";

// Postal codes: one per line or comma-separated (a space can be part of a code, e.g. "K1A 0B6"). Other types: commas or lines.
export const parseValues = (type, text) => String(text || "").split(/[,;\n]+/).map((v) => v.trim()).filter(Boolean);

const PLACEHOLDER = { zip: "53202\nK1A 0B6\nSW1A 1AA", city: "Toronto\nMontréal", state: "Ontario, Quebec", country: "CA, GB, Mexico" };
const EMPTY = { type: "zip", label: "", values: "", state: "", country: "", status: "waitlist" };
const SERVER_ERROR = {
  sanctioned_region: "This is a sanctioned country or area. It’s always blocked and can only be Off.",
  country_required: "Pick the country for this postal code, city or state/province.",
};

export default function RegionDialog({ open, onOpenChange, region, onSaved }) {
  const editing = !!region;
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!open) return;
    setError(null);
    setF(region ? { type: region.type, label: region.label || "", values: region.values.join("\n"),
      state: region.state || "", country: region.country || "", status: region.status } : EMPTY);
  }, [open, region]);
  const set = (k) => (v) => { setF((s) => ({ ...s, [k]: v })); setError(null); };
  const values = parseValues(f.type, f.values);
  const needsCountry = f.type !== "country";
  const locked = isSanctioned({ type: f.type, values, country: f.country, state: f.state });
  const status = locked ? "off" : f.status;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = editing ? { status, values, label: f.label.trim() }
        : { type: f.type, values, status, label: f.label.trim() || null, state: f.type === "city" ? f.state.trim() || null : null, country: needsCountry ? f.country : null };
      const res = await api(editing ? `/admin/regions/${region.id}` : "/admin/regions", { method: editing ? "PATCH" : "POST", body });
      onSaved(res.region, editing);
      onOpenChange(false);
    } catch (err) {
      setError({ code: err.code, text: SERVER_ERROR[err.code] || err.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-[20px] sm:max-w-lg" data-testid="region-dialog">
        <form onSubmit={submit} className="space-y-5">
          <DialogHeader><DialogTitle>{editing ? "Edit rule" : "Add a rule"}</DialogTitle>
            <DialogDescription data-testid="region-dialog-precedence">{PRECEDENCE_HELP}</DialogDescription></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label htmlFor="rg-type">Level</Label>
              <Select value={f.type} onValueChange={set("type")} disabled={editing}>
                <SelectTrigger id="rg-type" className="mt-2" data-testid="region-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REGION_TYPES.map((t) => <SelectItem key={t.key} value={t.key} data-testid={`region-type-option-${t.key}`}>{t.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label htmlFor="rg-label">Label</Label>
              <Input id="rg-label" value={f.label} onChange={(e) => set("label")(e.target.value)} placeholder="Ottawa core" className="mt-2" data-testid="region-label-input" /></div>
          </div>
          {needsCountry && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className={f.type === "city" ? "" : "sm:col-span-2"}><Label htmlFor="rg-country">Country</Label>
                <CountryPicker id="rg-country" value={f.country} onChange={set("country")} disabled={editing} className="mt-2" testId="region-country-picker" invalid={error?.code === "country_required"} /></div>
              {f.type === "city" && <div><Label htmlFor="rg-state">State / province <span className="cw-muted font-normal">(optional)</span></Label>
                <Input id="rg-state" value={f.state} onChange={(e) => set("state")(e.target.value)} disabled={editing} placeholder="Ontario or ON" className="mt-2" data-testid="region-state-input" /></div>}
              {editing && <p className="cw-muted text-xs sm:col-span-2" data-testid="region-country-locked-note">The country{f.type === "city" ? " and state/province" : ""} can’t be changed on a saved rule. Add a new rule instead.</p>}
            </div>
          )}
          <div><Label htmlFor="rg-values">{REGION_TYPES.find((t) => t.key === f.type).label}</Label>
            <Textarea id="rg-values" rows={4} value={f.values} onChange={(e) => set("values")(e.target.value)} placeholder={PLACEHOLDER[f.type]} className="mt-2" data-testid="region-values-input" />
            <p className="cw-muted mt-2 text-xs">{values.length} value{values.length === 1 ? "" : "s"}. Separate with commas or new lines.{f.type === "zip" ? " Any format, e.g. K1A 0B6." : ""}</p></div>
          <div><p className="mb-2 text-sm font-medium" id="rg-status-label">Status</p>
            <StatusControl value={status} onChange={set("status")} locked={locked} labelledBy="rg-status-label" testId="region-status-control" />
            {locked && <p className="cw-warning mt-3 flex items-start gap-2 text-sm" data-testid="region-sanctioned-note"><LockSimple size={16} className="mt-0.5 shrink-0" />This is a sanctioned region. It’s hard-blocked and can only be Off.</p>}</div>
          {error && <p className="cw-tone-error flex items-start gap-2 rounded-xl px-3 py-2 text-sm" role="alert" data-testid="region-dialog-error"><WarningOctagon size={16} className="mt-0.5 shrink-0" />{error.text}</p>}
          <DialogFooter><Button type="submit" disabled={busy || !values.length || (needsCountry && !f.country)} data-testid="region-save-button">{busy ? "Saving…" : editing ? "Save changes" : "Add rule"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
